<template>
    <div
        v-if="enabled"
        class="flex flex-col items-start self-stretch pt-6 mb-6"
    >
        <!-- Title + View all -->
        <div class="flex w-full pb-2 justify-between items-center">
            <h4 class="text-slate-800 dark:text-slate-100 text-base font-medium">
                Transfer Partners
            </h4>
            <a
                v-if="hasPartners"
                :href="transferPartnersPageUrl"
                class="flex items-center gap-1 text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:text-slate-700 dark:hover:text-slate-300 transition-colors duration-150 rounded"
            >
                View all
                <Icon icon="chevron-right" class="w-3 h-3" />
            </a>
        </div>

        <!-- Loading State -->
        <div
            v-if="loading"
            class="flex items-center justify-center py-8 w-full"
        >
            <LoadingState :loading="true" />
        </div>

        <!-- Transfer Partners List -->
        <template v-else>
            <div
                v-if="hasPartners"
                class="flex flex-col items-start self-stretch"
            >
                <div
                    v-for="(partner, index) in partners"
                    :key="partner.id"
                    class="flex h-[72px] py-4 items-center gap-4 self-stretch"
                    :class="{
                        'border-b border-slate-200 dark:border-slate-700':
                            index < partners.length - 1,
                    }"
                >
                    <!-- Logo -->
                    <div
                        class="flex w-14 h-6 px-3 justify-center items-center"
                    >
                        <img
                            v-if="programImgSrc(partner)"
                            :src="programImgSrc(partner)"
                            :alt="partner.program_name"
                            class="max-w-full max-h-full object-contain"
                        />
                        <span
                            v-else
                            class="text-xs text-slate-500 dark:text-slate-400 font-medium"
                        >
                            {{
                                partner.program_name
                                    .substring(0, 2)
                                    .toUpperCase()
                            }}
                        </span>
                    </div>

                    <!-- Name + Type -->
                    <div class="flex flex-col items-start flex-1">
                        <span
                            class="self-stretch text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                        >
                            {{ partner.program_name }}
                        </span>
                        <span
                            class="self-stretch text-slate-500 dark:text-slate-400 text-sm font-normal leading-5"
                        >
                            {{ formatProgramType(partner.program_type) }}
                        </span>
                    </div>

                    <!-- Ratios + Bonus -->
                    <div
                        class="flex h-[72px] py-[18px] flex-col justify-center items-end gap-0.5"
                    >
                        <!-- Ratios -->
                        <div class="flex items-center gap-2">
                            <span
                                v-if="partner.bonus_percentage"
                                class="text-slate-400 dark:text-slate-500 text-sm font-normal leading-5 line-through"
                            >
                                {{ partner.base_transfer_ratio }}
                            </span>
                            <span
                                class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                            >
                                {{ partner.effective_transfer_ratio }}
                            </span>
                            <TransferRateBreakdown :rates="partner.rates" />
                        </div>
                        <!-- Bonus Pill -->
                        <span
                            v-if="partner.bonus_percentage"
                            class="inline-flex py-[2px] px-3 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-sm font-medium leading-5 text-center"
                        >
                            +{{ partner.bonus_percentage }}% until
                            {{ partner.bonus_end_date }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div
                v-else
                class="flex pt-2 flex-col items-start self-stretch rounded-lg"
            >
                <p class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5">
                    No programs to exchange
                </p>
            </div>
        </template>
    </div>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import TransferRateBreakdown from '@/components/transfer-partners/TransferRateBreakdown.vue';

const props = defineProps({
    partners: {
        type: Array,
        required: true,
    },
    loading: {
        type: Boolean,
        required: true,
    },
    hasPartners: {
        type: Boolean,
        required: true,
    },
    transferPartnersPageUrl: {
        type: String,
        required: true,
    },
    enabled: {
        type: Boolean,
        required: true,
    },
    programImgSrc: {
        type: Function,
        required: true,
    },
    formatProgramType: {
        type: Function,
        required: true,
    },
});
</script>
